import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { Offer } from '../../../model/offer';
import { SharedService, UiOfferTab } from '../../../_services/shared.service';
import { ShoppingCartService } from '../../../_services/shopping-cart.service';
import { ThemeService } from '../../../_services/theme.service';
import { OfferCategoryService } from '../../../_services/offer-category.service';
import { UiNewOfferPhotoTermsComponent } from '../ui-new-offer-photo-terms/ui-new-offer-photo-terms.component';

@Component({
  selector: 'ui-new-offer-card-addon',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ui-new-offer-card-addon.component.html',
  styleUrl: './ui-new-offer-card-addon.component.scss',
})
export class UiNewOfferCardAddonComponent implements OnInit {
  private bo = inject(BreakpointObserver);
  private destroyRef = inject(DestroyRef);

  @Input() offers!: Offer[] | null;
  @Input() offer!: Offer | null;
  @Input() index = 0;
  @Input() viewMoreToggle = false;
  @Input() propertyId = '';
  @Input() offerAliasId = '';
  @Input() viewMode: 'tiles' | 'list' = 'tiles';

  isHandset = false;


  constructor(
    public sharedService: SharedService,
    public themeService: ThemeService,
    public shoppingCartService: ShoppingCartService,
    private offerCategoryService: OfferCategoryService,
  ) {
    const sub = this.bo.observe([Breakpoints.HandsetPortrait]).subscribe(state => {
      this.isHandset = state.matches;
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  ngOnInit(): void {
    const activeTab = this.sharedService.getOfferCurrentActiveFilter() as UiOfferTab;
    this.sharedService.markUpgradeTabSeen(activeTab);
  }

  isTilesMode(): boolean {
    return this.viewMode === 'tiles';
  }

  shouldUseVerticalLayout(): boolean {
    return this.isTilesMode() || this.isHandset;
  }


  goToEarlyCheckInTab(): void {
    this.sharedService.navigateToOfferTab('ECI');
  }

  goToLateCheckoutTab(): void {
    this.sharedService.navigateToOfferTab('LCO');
  }

  public goToAddonTab(): void {
    this.sharedService.navigateToOfferTab('ANCILLARY');
  }

  public checkOut() {
    this.shoppingCartService.openCheckOutOfferTermsModal(this.offerAliasId, this.propertyId, UiNewOfferPhotoTermsComponent);
  }

  goToNextTab(): void {

    const offersInCart = this.shoppingCartService.getNumberOfOffersInShoppingCart(this.offerAliasId);
    const next = this.sharedService.getNextUpgradeAction(offersInCart);

    if (!next) return;
    if (next.action === 'checkout') {
      this.checkOut();
      return;
    }

    this.sharedService.navigateToOfferTab(next.tab);
  }

  addToCartAndContinue(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    try {
      this.addOfferToCart();
    } finally {
      this.goToNextTab();
    }
  }

  addOfferToCart(): void {
    if (this.offer) {
      this.shoppingCartService.addOfferToCart(this.offer, this.offerAliasId, true);
    }
  }

  removeOfferFromCart(): void {
    if (this.offer) {
      this.shoppingCartService.removeOfferFromCart(this.offer, this.offerAliasId);
    }
  }

  requestUpgrade(): void {
    if (this.offer) {
      this.shoppingCartService.addOfferToCart(this.offer, this.offerAliasId, false);
      this.shoppingCartService.openCheckOutOfferTermsModal(this.offerAliasId, this.propertyId);
    }
  }

  checkIfOfferInCart(): boolean {
    return this.shoppingCartService.checkIfOfferInCart(
      this.offerAliasId,
      this.offer?.offer_set_id || '',
      this.offer?.offer_id || '',
    );
  }

  checkIfDisabled(): boolean {
    const offers = this.shoppingCartService.getOffersByAliasId(this.offerAliasId);
    
    // Offers with ofr_category 'ANCILLARY' are allowed to have multiple 
    // entries in the cart, so they are never disabled
    if (this.offerCategoryService.isOfferInCategory(this.offers?.[0], 'ANCILLARY')) {
      return false;
    }


    if (!offers || !this.offer) {
      return false;
    }

    const exactMatch = offers.find(entry =>
      entry.offerSetId === this.offer!.offer_set_id && 
      entry.offerId === this.offer!.offer_id
    );

    const offerSetMatch = offers.find(entry =>
      entry.offerSetId === this.offer!.offer_set_id
    );

    // Disabled if there's a different offer in the same set
    return !exactMatch && !!offerSetMatch;
  }
}
